import { useEffect, useState } from 'react';
import { useStore } from '@/store/useStore';
import { RaffleCard } from '@/components/RaffleCard';
import { RaffleStatus } from '@/types';
import { Loader2, TrendingUp, Users, Trophy } from 'lucide-react';

export const HomePage = () => {
  const { user, raffles, loading, fetchRaffles, fetchUser } = useStore();
  const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');

  useEffect(() => {
    fetchUser();
    fetchRaffles();
  }, []);

  const activeRaffles = raffles.filter((r) => r.status === RaffleStatus.ACTIVE);
  const completedRaffles = raffles.filter((r) => r.status === RaffleStatus.COMPLETED);
  const totalPrizes = activeRaffles.reduce((sum, r) => sum + (r.prizes?.length || 0), 0);
  const totalParticipants = activeRaffles.reduce((sum, r) => sum + r.participants_count, 0);

  const displayedRaffles = activeTab === 'active' ? activeRaffles : completedRaffles;

  return (
    <div className="pb-20">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white p-6 rounded-b-3xl shadow-lg">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-2">
            Добро пожаловать!
          </h1>
          <p className="text-blue-100 mb-4">
            Участвуйте в розыгрышах и выигрывайте призы
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white/10 backdrop-blur rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Trophy size={16} className="text-yellow-300" />
                <div className="text-xs opacity-80">Призов</div>
              </div>
              <div className="text-lg font-bold">
                {totalPrizes}
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Users size={16} className="text-green-300" />
                <div className="text-xs opacity-80">Игроки</div>
              </div>
              <div className="text-lg font-bold">
                {totalParticipants}
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp size={16} className="text-pink-300" />
                <div className="text-xs opacity-80">Активных</div>
              </div>
              <div className="text-lg font-bold">
                {activeRaffles.length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Raffles Section with Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab('active')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-colors ${
              activeTab === 'active'
                ? 'bg-blue-500 text-white shadow-lg'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            🎯 Сейчас
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-colors ${
              activeTab === 'completed'
                ? 'bg-blue-500 text-white shadow-lg'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            🏆 Прошедшие
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="animate-spin text-telegram-button" size={32} />
          </div>
        ) : displayedRaffles.length === 0 ? (
          <div className="text-center py-12">
            <Trophy size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">
              {activeTab === 'active' ? 'Нет активных розыгрышей' : 'Нет завершенных розыгрышей'}
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {displayedRaffles.map((raffle) => (
              <RaffleCard key={raffle.id} raffle={raffle} showWinner={activeTab === 'completed'} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
